import { useVirtualizer } from "@tanstack/react-virtual"
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useAiTeammateContext, useMessagesContext, useThreadsContext } from "../../hooks/context"
import useMinuteTicker from "../../hooks/minute-ticker"
import AiTeammateMessage from "../message"
import { MessageSkeleton } from "../message/message-skeleton"
import AiTeammateNoMessage from "../no-message"
import AiTeammateThinking from "../thinking"
import "./style.scss"

export default function AiTeammateMessagesPanel() {
  const panelRef = useRef(null)
  const skeletonRef = useRef(null)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const scrollHeightBeforeFetchRef = useRef(0)
  const { isDrawerResizing, hasError, error } = useAiTeammateContext()
  const { isThreadOpen, threadId } = useThreadsContext()
  const { messages, hasMessages, fetchNextPage, isFetchingNextPage, hasNextPage, isSendingMessage, lastAssistantMessageId } = useMessagesContext()

  // Using a ticker to refresh the time of the messages every minute, since we are displaying relative times
  const { lastTickTime, startTicker, stopTicker } = useMinuteTicker()

  // Reset scroll state when thread is opened or closed
  useEffect(() => {
    setHasScrolledToBottom(false)
    scrollHeightBeforeFetchRef.current = 0
  }, [isThreadOpen])

  useEffect(() => {
    if (hasMessages) {
      startTicker()
    }

    return () => {
      stopTicker()
    }
  }, [hasMessages, startTicker, stopTicker])

  // Memoize virtualizer callbacks to prevent re-initialization
  const getScrollElement = useCallback(() => {
    return isDrawerResizing ? null : panelRef.current
  }, [isDrawerResizing])

  const getItemKey = useCallback((index) => {
    return messages[index]?.id || `message-${index}`
  }, [messages])

  const estimateSize = useCallback(() => 100, [])

  const virtualizer = useVirtualizer({
    count: messages.length,
    // This temporarily disables items measurement calculation while the drawer is resizing
    // Without it we get "ResizeObserver" errors as it triggers too many layout changes
    getScrollElement,
    getItemKey,
    estimateSize,
    useAnimationFrameWithResizeObserver: true,
    overscan: 5,
  })

  const totalMessageListHeight = virtualizer.getTotalSize()
  const virtualMessagesList = virtualizer.getVirtualItems()

  // Height of the skeleton loader at the top
  const skeletonHeight = hasNextPage ? 70 : 0

  // Preserve scroll position when loading older messages
  useEffect(() => {
    const panel = panelRef.current
    if (!panel || !isFetchingNextPage) return

    // Save distance from bottom before new messages load
    scrollHeightBeforeFetchRef.current = panel.scrollHeight - panel.scrollTop
  }, [isFetchingNextPage])

  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!panel || !scrollHeightBeforeFetchRef.current) return

    // Restore scroll position to keep same message visible
    panel.scrollTop = panel.scrollHeight - scrollHeightBeforeFetchRef.current
    scrollHeightBeforeFetchRef.current = 0
  }, [messages.length])

  // Load more messages when skeleton becomes visible
  useEffect(() => {
    if (!skeletonRef.current || !panelRef.current || !isThreadOpen || !hasNextPage || isFetchingNextPage || !hasScrolledToBottom) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage()
        }
      },
      {
        root: panelRef.current,
        rootMargin: "0px 0px",
        threshold: 0.01,
      },
    )

    observer.observe(skeletonRef.current)
    return () => observer.disconnect()
  }, [isThreadOpen, hasNextPage, isFetchingNextPage, fetchNextPage, hasScrolledToBottom])

  // Scroll to bottom when drawer opens or new messages arrive
  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!panel || !isThreadOpen) return

    // Double scroll to account for message measurement
    panel.scrollTo({ top: panel.scrollHeight })
    const rafId = requestAnimationFrame(() => {
      panel.scrollTo({ top: panel.scrollHeight })
      if (!hasScrolledToBottom) {
        setHasScrolledToBottom(true)
      }
    })

    return () => cancelAnimationFrame(rafId)
  }, [isThreadOpen, isSendingMessage, hasError, lastAssistantMessageId, hasScrolledToBottom])

  if (!threadId && !hasMessages && !hasError) {
    return <AiTeammateNoMessage />
  }

  return <div className="ai-teammate-messages-panel" ref={panelRef}>
    <div style={{ height: (totalMessageListHeight + skeletonHeight) || "100%", width: "100%", position: "relative" }}>
      {/* Skeleton loader at the top - shows when more messages are available */}
      {hasNextPage && (
        <div
          ref={skeletonRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            padding: "0.5rem 1rem",
          }}
        >
          <MessageSkeleton />
        </div>
      )}

      {hasMessages && virtualMessagesList.map((virtualMessage) => {
        const message = messages[virtualMessage.index]

        return <div
          key={virtualMessage.key}
          data-index={virtualMessage.index}
          ref={virtualizer.measureElement}
          style={{
            position: "absolute",
            transform: `translateY(${virtualMessage.start + skeletonHeight}px)`,
            width: "100%",
          }}
        >
          <AiTeammateMessage message={message} messageTimeRefresher={lastTickTime} />
        </div>
      })}
      {hasError && <div>
        <div style={{
          position: "absolute",
          transform: `translateY(${totalMessageListHeight + skeletonHeight}px)`,
          width: "100%",
        }}
        >
          <AiTeammateMessage
            message={{
              role: "assistant",
              error,
            }}
            messageTimeRefresher={lastTickTime}
          />
        </div>
      </div>}
      {isSendingMessage && <div style={{
          position: "absolute",
          transform: `translateY(${totalMessageListHeight + skeletonHeight}px)`,
          width: "100%",
          paddingBottom: "1rem",
        }}
      >
        <AiTeammateThinking />
      </div>}
    </div>
  </div>
}
